import { useEffect, useRef, useState } from 'react';
import { listenBest } from '../lib/listen';
import { canListen } from '../lib/speech';
import { useElena } from '../state';
import { Icon } from './Icon';
import { Waveform, type WaveMode } from './Waveform';
import { ElenaAvatar } from './ElenaAvatar';
import { RevealText } from './RevealText';
import { useOpenAnimation } from '../lib/motion';

const MAX_SILENT_ROUNDS = 3;

/**
 * Hands-free conversation: Elena answers out loud, then the microphone opens again by itself.
 * Ends with "ćao" / "kraj razgovora", the button, Escape, or a few rounds of silence.
 */
export function TalkMode() {
  const { talk, thinking, messages, spokenTurn, send, setTalk, connection } = useElena();
  const [heard, setHeard] = useState('');
  const [listening, setListening] = useState(false);
  const [round, setRound] = useState(0);
  const [note, setNote] = useState<string | null>(null);
  const silent = useRef(0);
  const closeRef = useRef<HTMLButtonElement>(null);
  const overlay = useRef<HTMLDivElement>(null);
  useOpenAnimation(overlay, talk);

  const lastElena = [...messages].reverse().find((m) => m.role === 'elena');
  const lastUser = [...messages].reverse().find((m) => m.role === 'user');
  const speakingNow = Boolean(talk && !thinking && lastElena && lastElena.id !== spokenTurn);
  const ready = talk && !thinking && connection === 'online' && !speakingNow;

  useEffect(() => {
    if (talk) {
      silent.current = 0;
      setNote(null);
      closeRef.current?.focus();
    }
  }, [talk]);

  useEffect(() => {
    if (!ready || !canListen()) return;
    let cancelled = false;
    let got = false;
    setHeard('');
    setListening(true);
    const stop = listenBest({
      onInterim: (t) => !cancelled && setHeard(t),
      onFinal: (t, alternatives) => {
        if (cancelled) return;
        got = true;
        silent.current = 0;
        send(t, true, alternatives);
      },
      onError: () => undefined,
      onEnd: () => {
        if (cancelled) return;
        setListening(false);
        if (got) return;
        silent.current += 1;
        if (silent.current >= MAX_SILENT_ROUNDS) {
          setNote('Ne čujem te, pa sam završila razgovor.');
          setTalk(false);
        } else {
          setRound((r) => r + 1);
        }
      },
    });
    return () => {
      cancelled = true;
      setListening(false);
      stop();
    };
  }, [ready, round, send, setTalk]);

  useEffect(() => {
    if (!note || talk) return;
    const t = window.setTimeout(() => setNote(null), 5000);
    return () => window.clearTimeout(t);
  }, [note, talk]);

  useEffect(() => {
    if (!talk) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setTalk(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [talk, setTalk]);

  if (!talk)
    return note ? (
      <p className="talk-toast" role="status">
        {note}
      </p>
    ) : null;

  const mode: WaveMode = listening
    ? 'listening'
    : thinking
      ? 'thinking'
      : speakingNow
        ? 'speaking'
        : 'idle';
  const label = !canListen()
    ? 'Glas radi u Chrome-u i Edge-u'
    : connection !== 'online'
      ? 'Povezujem se…'
      : listening
        ? 'Slušam te'
        : thinking
          ? 'Razmišljam'
          : speakingNow
            ? 'Elena priča'
            : 'Trenutak…';

  return (
    <div
      ref={overlay}
      className="talk-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="talk-title"
    >
      <div className="talk-inner">
        <header className="talk-head">
          <span className="talk-badge">
            <Icon name="talk" size={16} /> <span id="talk-title">Razgovor sa Elenom</span>
          </span>
          <span className="muted small">Reci „ćao“ kad završiš</span>
        </header>

        <div className="talk-body" aria-live="polite">
          <ElenaAvatar mode={mode} />
          <p className="talk-label">{label}</p>
          {listening ? (
            <p className="talk-heard">{heard || 'Pričaj…'}</p>
          ) : (
            <>
              {lastUser && <p className="talk-user">{lastUser.text}</p>}
              <RevealText className="talk-reply" text={lastElena?.text ?? 'Hajde, pričaj mi.'} />
            </>
          )}
        </div>

        <Waveform mode={mode} />

        <div className="talk-actions">
          <button
            ref={closeRef}
            type="button"
            className="btn danger"
            onClick={() => setTalk(false)}
          >
            <Icon name="x" size={16} /> Završi razgovor
          </button>
        </div>
      </div>
    </div>
  );
}
